import { Link } from "react-router";

interface ErrorPageProps {
  /** When rendered by ErrorBoundary or router errorElement */
  error?: Error | unknown;
}

/**
 * Friendly error page: "Oops, we spilled some tea." with the matcha cup image.
 * Used by the root ErrorBoundary and by React Router errorElement.
 */
export function ErrorPage({ error }: ErrorPageProps) {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6"
      style={{ backgroundColor: "#F7F5F1", fontFamily: "'Jost', sans-serif" }}
    >
      <div className="text-center max-w-md">
        <img
          src="/error.png"
          alt=""
          className="w-48 h-auto mx-auto mb-6"
          style={{ maxHeight: "240px", objectFit: "contain" }}
        />
        <p
          className="tracking-[0.3em] uppercase mb-2"
          style={{ fontSize: "12px", color: "#C7A76C" }}
        >
          Oops
        </p>
        <h1
          className="tracking-[0.1em] mb-4"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "clamp(24px, 4vw, 32px)",
            fontWeight: 400,
            color: "#2D2A26",
          }}
        >
          We spilled some tea.
        </h1>
        <p
          className="leading-[1.8] mb-8"
          style={{ fontSize: "15px", fontWeight: 300, color: "#6F675F" }}
        >
          Something went wrong. No need to worry — grab a fresh cup and try again.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <Link
            to="/"
            className="px-8 py-3 tracking-[0.2em] uppercase rounded-full transition-all duration-300"
            style={{
              fontSize: "12px",
              fontWeight: 400,
              backgroundColor: "#6F7F63",
              color: "#FFFFFF",
            }}
          >
            Back to Home
          </Link>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-8 py-3 tracking-[0.2em] uppercase rounded-full transition-all duration-300 border"
            style={{
              fontSize: "12px",
              fontWeight: 400,
              color: "#6F7F63",
              borderColor: "#6F7F63",
              backgroundColor: "transparent",
            }}
          >
            Reload Page
          </button>
        </div>
        {import.meta.env.DEV && error instanceof Error && (
          <pre
            className="mt-8 text-left bg-[#2D2A26] text-white/70 rounded-xl p-4 overflow-x-auto"
            style={{ fontSize: "11px" }}
          >
            {error.message}
          </pre>
        )}
      </div>
    </div>
  );
}
